'use client';

import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslations } from "next-intl";
import { mutate } from "swr";
import {
  getActiveWorkspaceScopeKey,
  resolveActiveWorkspaceContext,
  syncWorkspaceStoreToLocation,
} from "@/lib/active-workspace";
import { shouldSkipAuth, validateClientAuth } from '@/lib/client-auth';
import { authorize } from '@/services/auth';
import {
  CONNECTOR_HOME_GUIDANCE_SCREEN,
  canAccessConnectorScreen,
  hasOtenCopilotAccess,
} from "@/lib/connector-access";
import {
  getOnboardingProgressCacheKey,
  prefetchOnboardingProgress,
} from "@/services/onboarding-service";
import { fetchUserWorkspaces } from "@/services/workspace-service";
import { useUserStore } from '@/store/user/user-store';
import { useWorkspaceStore } from '@/store/workspace/workspace-store';
import { AUTH_CONFIG } from '@/config/auth';

interface AuthProviderProps extends PropsWithChildren {
  /**
   * Paths that should skip automatic authorization
   */
  skipPaths?: string[];
}

/**
 * AuthProvider component that automatically initiates authentication
 * when the app loads and the user is not authenticated.
 * Similar to chat-platform but pointing to localhost services.
 */
export const AuthProvider: FC<AuthProviderProps> = ({
  children,
  skipPaths = AUTH_CONFIG.skipPaths,
}) => {
  const t = useTranslations("AuthProvider");
  const [isChecking, setIsChecking] = useState(true);
  const [hasChecked, setHasChecked] = useState(false);
  const lastWorkspaceContextKeyRef = useRef<string>('');

  // Use Zustand store for auth state
  const {
    setUser,
    setAuthenticated,
    setInitialized,
    beginWorkspaceEntitlementsLoad,
    setWorkspaceEntitlements,
    failWorkspaceEntitlementsLoad,
    isSignedIn,
  } = useUserStore();
  const selectedWorkspace = useWorkspaceStore((state) => state.selectedWorkspace);
  const workspacesLoaded = useWorkspaceStore((state) => state.workspacesLoaded);

  useEffect(() => {
    const checkAuthAndInitiate = async () => {
      // Prevent multiple simultaneous checks
      if (hasChecked) {
        setIsChecking(false);
        return;
      }

      // Skip auth if disabled
      if (!AUTH_CONFIG.enableAuth) {
        setAuthenticated(true);
        setInitialized(true);
        setHasChecked(true);
        setIsChecking(false);
        return;
      }

      // Skip if on excluded paths
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const shouldSkip = shouldSkipAuth(currentPath, skipPaths);

        if (shouldSkip) {
          console.log('[AuthProvider] Skipping auth for path:', currentPath);
          setIsChecking(false);
          setHasChecked(true);
          return;
        }

        // Check if we're returning from an OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('code') || urlParams.has('state') || currentPath.includes('callback')) {
          console.log('[AuthProvider] OAuth callback detected, skipping auth check');
          setIsChecking(false);
          setHasChecked(true);
          return;
        }
      }

      try {
        let didPreloadWorkspaces = false;
        let activeWorkspaceContext = resolveActiveWorkspaceContext();

        try {
          console.log("[AuthProvider] Fetching workspaces before /me...");
          const workspaces = await fetchUserWorkspaces();
          useWorkspaceStore.getState().setWorkspaces(workspaces);
          syncWorkspaceStoreToLocation();
          activeWorkspaceContext = resolveActiveWorkspaceContext();
          didPreloadWorkspaces = true;
        } catch (workspaceError) {
          console.error("[AuthProvider] Failed to preload workspaces:", workspaceError);
        }

        console.log('[AuthProvider] Checking authentication status...');

        const authResult = await validateClientAuth({
          workspaceId: activeWorkspaceContext.workspaceId,
          organizationId: activeWorkspaceContext.organizationId,
          skipWorkspaceBootstrap: didPreloadWorkspaces,
        });

        if (authResult.isAuthenticated && authResult.user) {
          const workspaceKey =
            activeWorkspaceContext.organizationId &&
            activeWorkspaceContext.workspaceId
              ? `${activeWorkspaceContext.organizationId}:${activeWorkspaceContext.workspaceId}`
              : null;
          if (workspaceKey) {
            lastWorkspaceContextKeyRef.current = workspaceKey;
            setWorkspaceEntitlements(workspaceKey, authResult.user);
          } else {
            setUser(authResult.user);
          }

          if (
            hasOtenCopilotAccess(authResult.user.features) &&
            canAccessConnectorScreen(
              authResult.user.screens,
              authResult.user.features,
              CONNECTOR_HOME_GUIDANCE_SCREEN,
            )
          ) {
            const onboardingResponse = await prefetchOnboardingProgress();
            const onboardingScopeKey = getActiveWorkspaceScopeKey();
            if (onboardingScopeKey !== "pending") {
              mutate(
                getOnboardingProgressCacheKey(onboardingScopeKey),
                onboardingResponse,
                false,
              );
            }
          }

          console.log('[AuthProvider] User is authenticated:', authResult.user);
          setAuthenticated(true);
          setInitialized(true);
          setHasChecked(true);
          setIsChecking(false);
          return;
        }

        console.log('[AuthProvider] User not authenticated, initiating login redirect...');
        
        // Get current path for redirect after auth
        const currentPath =
          typeof window !== 'undefined'
            ? window.location.pathname +
              window.location.search +
              window.location.hash
            : '/dashboard';
        
        // Initiate authentication (this will redirect to localhost:81)
        authorize({
          next: currentPath,
          referer: typeof window !== 'undefined' ? window.location.href : undefined,
          project_id: '1',
        });
        
        // Don't set hasChecked to true since we're redirecting
      } catch (error) {
        console.error('[AuthProvider] Error during auth check/initiation:', error);
        
        // On error, still try to initiate auth
        console.log('[AuthProvider] Error occurred, attempting login redirect...');
        try {
          const fallbackPath =
            typeof window !== 'undefined'
              ? window.location.pathname +
                window.location.search +
                window.location.hash
              : '/dashboard';
          authorize({
            next: fallbackPath,
            project_id: '1',
          });
        } catch (authError) {
          console.error('[AuthProvider] Failed to initiate auth:', authError);
          setHasChecked(true);
          setIsChecking(false);
        }
      }
    };

    checkAuthAndInitiate();
  }, [
    hasChecked,
    setAuthenticated,
    setInitialized,
    setUser,
    setWorkspaceEntitlements,
    skipPaths,
  ]);

  useEffect(() => {
    if (!hasChecked || !isSignedIn || !workspacesLoaded || !selectedWorkspace) {
      return;
    }

    const workspaceContextKey = `${selectedWorkspace.organization_id}:${selectedWorkspace.workspace_id}`;
    if (workspaceContextKey === lastWorkspaceContextKeyRef.current) {
      return;
    }
    lastWorkspaceContextKeyRef.current = workspaceContextKey;
    beginWorkspaceEntitlementsLoad(workspaceContextKey);

    let isCancelled = false;

    const refreshAuthContext = async () => {
      try {
        const authResult = await validateClientAuth({
          workspaceId: selectedWorkspace.workspace_id,
          organizationId: selectedWorkspace.organization_id,
          skipWorkspaceBootstrap: true,
        });

        if (!isCancelled && authResult.isAuthenticated && authResult.user) {
          // Publish the current workspace entitlement scope before any protected
          // Connector request. The fetcher rejects domain calls while the scope
          // is still marked as loading.
          setWorkspaceEntitlements(workspaceContextKey, authResult.user);

          if (
            hasOtenCopilotAccess(authResult.user.features) &&
            canAccessConnectorScreen(
              authResult.user.screens,
              authResult.user.features,
              CONNECTOR_HOME_GUIDANCE_SCREEN,
            )
          ) {
            const onboardingResponse = await prefetchOnboardingProgress();
            const onboardingScopeKey = getActiveWorkspaceScopeKey();
            if (onboardingScopeKey !== "pending") {
              mutate(
                getOnboardingProgressCacheKey(onboardingScopeKey),
                onboardingResponse,
                false,
              );
            }
          }
          return;
        }

        if (!isCancelled) {
          failWorkspaceEntitlementsLoad(workspaceContextKey);
        }
      } catch (error) {
        console.error('[AuthProvider] Failed to refresh auth context by workspace:', error);
        if (!isCancelled) {
          failWorkspaceEntitlementsLoad(workspaceContextKey);
        }
      }
    };

    refreshAuthContext();

    return () => {
      isCancelled = true;
    };
  }, [
    hasChecked,
    beginWorkspaceEntitlementsLoad,
    failWorkspaceEntitlementsLoad,
    isSignedIn,
    selectedWorkspace,
    setWorkspaceEntitlements,
    workspacesLoaded,
  ]);

  // Show loading spinner while checking auth
  if (isChecking) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            {t("checkingAuthentication")}
          </p>
        </div>
        <style jsx>{`
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthProvider;
